import { useEffect } from 'react';
import { NavigationType, useLocation, useNavigationType } from 'react-router';
import { ChatProvider } from '@/ui/features/chat/ChatProvider';
import { useCaseMention } from '@/ui/features/chat/hooks/useCaseMention';
import { AppRoutes } from '@/constants';
import { ChatView } from './Chat';
import { useChatPageParams } from './hooks/useChatPageParams';
import { clearChat, selectSessionChatDocuments, useAppDispatch, useAppSelector } from '@/store';

export const ChatPage = () => {
  const dispatch = useAppDispatch();
  const caseMention = useCaseMention();
  const sessionDocuments = useAppSelector(selectSessionChatDocuments);
  // Add the case-mention matter to the send context, so `useChat` creates the chat with it.
  const { defaultSendOptions, resolveMatterQueryDocuments } = useChatPageParams({
    caseMentionMatterId: caseMention.selectedCase?.matterId,
  });

  const { pathname, key: locationKey } = useLocation();
  const navigationType = useNavigationType();
  const isNewChatRoute = pathname === `/${AppRoutes.Chat}`;
  // Arriving at `/chat` starts a new chat, except by replace: the case page's send hands its chat over that way.
  useEffect(() => {
    if (isNewChatRoute && navigationType !== NavigationType.Replace) {
      dispatch(clearChat());
    }
  }, [dispatch, isNewChatRoute, locationKey, navigationType]);
  // Clear the case mention when leaving the new-chat page.
  useEffect(() => {
    if (!isNewChatRoute && caseMention.selectedCase) {
      caseMention.clearCase();
    }
  }, [isNewChatRoute, caseMention]);

  // With a linked case, start a new chat for it instead of using the store's chat id. Unless files
  // were already uploaded: they belong to an existing chat, and a new one would reject them. The
  // matter is still sent as context.
  const disableStoreChatId = Boolean(caseMention.selectedCase) && sessionDocuments.length === 0;

  return (
    <ChatProvider
      options={{ defaultSendOptions, resolveMatterQueryDocuments, disableStoreChatId, unsentPromptScope: 'chat-page' }}
    >
      <ChatView caseMention={caseMention} />
    </ChatProvider>
  );
};
